import * as React from "react";
import {VeboxForm} from "../../../components/form/VeboxForm";
import {
    ProcessStage,
    ProcessStageArrow,
    ProcessStageArrowDirection,
    ProcessStageOrder,
    useProcessController
} from "../../Processes";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {InputAddon, SelectInputOption, TextInputProps, Tooltip} from "@vebox/etui-core";
import {
    INPUT_BORDER_BOX,
    SELECT_BORDER_BOX,
    SelectStyles,
    Styles
} from "../../../components/common/etui_components_styles";
import {VeboxUser} from "../../../providers/VeboxUser";
import {FormCardFooter} from "../../common";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import useRestData from "../../../hooks/useRestData";
import {isValidEmailAddress, isValidUsername} from "../../../account/AccountUtils";
import {getUsernameAndEmailValidity} from "../../../account/AccountController";
import {ValidityState} from "../../../account/Account.types";
import {ValueToUpload} from "../UserCreation";

declare var AppTranslations: AppTranslations;

const INPUT_BORDER_BOX_WITH_MARGIN: Styles = {
    ...INPUT_BORDER_BOX,
    root: {
        mb: 10,
    },
};

const SELECT_BORDER_BOX_WITH_MARGIN: SelectStyles = {
    ...SELECT_BORDER_BOX,
    root: {
        mb: 10,
    },
};

interface LocaleInfo {
    name: string;
    displayName: string;
}

const USER_INFO_STEP_NUMBER = 1;

const supportRestDataOptions = {
    withAuthentication: false,
    mapResult: x => x.list
}

export const UserInfoStage: React.FC = () => {

    const processController = useProcessController();

    const [isFormValid, setIsFormValid] = React.useState<ValidityState>({all: false});

    const [loading, setLoading] = React.useState<boolean>(false);

    const [info, setInfo] = VeboxForm.useFormValueState<any>(ValueToUpload.USER_INFO);

    const {data: locales = []} = useRestData<LocaleInfo[]>("support/locales", {}, supportRestDataOptions);
    const {data: timezones = []} = useRestData<string[]>("support/timezones", {}, supportRestDataOptions);

    const user = VeboxUser.useCurrentUser();

    const resetValues = () => setInfo({});

    const goToNextStep = () => {
        setLoading(true);
        getUsernameAndEmailValidity(info.username, info.email)
            .then(r => {
                const companyGuid = user.company.parentGuid ?? user.company.guid;
                const mailAlreadyInuse = r.parentCompanyGuids.includes(companyGuid);
                if (!r.usernameValid || r.usernameUsedUp || mailAlreadyInuse) {
                    setIsFormValid({
                        all: false,
                        usernameAlreadyInUse: r.usernameUsedUp,
                        usernameValid: r.usernameValid,
                        emailAlreadyInUse: mailAlreadyInuse,
                        emailValid: true
                    });
                } else {
                    processController.goTo(processController.position + 1);
                }
                setLoading(false);
            }).catch((error) => {
            if ((error.message as string).toLowerCase().includes("validation failed")
                && (error.message as string).toLowerCase().includes("email")) {
                setIsFormValid({
                    emailValid: false,
                    usernameAlreadyInUse: false,
                    usernameValid: true,
                    emailAlreadyInUse: false,
                    all: false
                })
            } else {
                setIsFormValid({all: false});
            }
            setLoading(false);
        })
    };

    React.useEffect(() => {

        const isFilled = Boolean(info?.name)
            && Boolean(info?.surname)
            && Boolean(info?.username)
            && Boolean(info?.email)
            && Boolean(info?.locale)
            && Boolean(info?.timezone);

        const isEmailValid = info?.email !== "" && isValidEmailAddress(info?.email);
        const isUsernameValid = info?.username !== "" && isValidUsername(info?.username);

        if (isFilled !== isFormValid.all || isEmailValid !== isFormValid.emailValid || isUsernameValid !== isFormValid.usernameValid) {
            setIsFormValid({
                all: isFilled,
                emailValid: isEmailValid,
                usernameValid: isUsernameValid,
            });
        }

        if (processController.reachablePosition > USER_INFO_STEP_NUMBER - 1) {
            processController.setReachablePosition(USER_INFO_STEP_NUMBER - 1);
        }

    }, [info]);

    return (
        <ProcessStage
            stepNumber={USER_INFO_STEP_NUMBER}
            title={AppTranslations.userCreationProcess.infoStageTitle}
            stageOrder={ProcessStageOrder.FIRST}
        >
            <div className={"flx-row"}>
                <ProcessStageArrow stepNumber={USER_INFO_STEP_NUMBER} direction={ProcessStageArrowDirection.BACKWARD}/>
                <VeBoxCard className={"form-card"} style={{height: "515px"}}>
                    <div>
                        <VeboxForm.Section name={"user_info"}>
                            <VeboxForm.TextInput
                                name={"name"}
                                label={AppTranslations.userCreationProcess.name}
                                styles={INPUT_BORDER_BOX_WITH_MARGIN as any}
                                autoComplete={"off"}
                            />
                            <VeboxForm.TextInput
                                name={"surname"}
                                label={AppTranslations.userCreationProcess.surname}
                                styles={INPUT_BORDER_BOX_WITH_MARGIN as any}
                                autoComplete={"off"}
                            />
                            <TextInputsWithErrorMessages
                                name={"username"}
                                label={AppTranslations.userCreationProcess.username}
                                autoComplete={"off"}
                                isWrong={isFormValid.usernameAlreadyInUse || !isFormValid.usernameValid}
                                errorMessage={!isFormValid.usernameValid
                                    ? AppTranslations.userCreationProcess.usernameNotValid
                                    : AppTranslations.userCreationProcess.usernameTaken}
                            />
                            <TextInputsWithErrorMessages
                                name={"email"}
                                label={AppTranslations.userCreationProcess.email}
                                type={"email"}
                                autoComplete={"off"}
                                isWrong={isFormValid.emailAlreadyInUse || !isFormValid.emailValid}
                                errorMessage={!isFormValid.emailValid
                                    ? AppTranslations.userCreationProcess.emailInvalidFormat
                                    : AppTranslations.userCreationProcess.emailTaken
                                }
                            />
                            <VeboxForm.SelectInput
                                name={"locale"}
                                label={AppTranslations.userCreationProcess.locale}
                                styles={SELECT_BORDER_BOX_WITH_MARGIN}
                                options={locales.map<SelectInputOption>(t => ({value: t.name, label: t.displayName}))}
                                searchable={true}
                            />
                            <VeboxForm.SelectInput
                                name={"timezone"}
                                label={AppTranslations.userCreationProcess.timezone}
                                styles={SELECT_BORDER_BOX_WITH_MARGIN}
                                options={timezones.map<SelectInputOption>(l => ({value: l, label: l}))}
                                searchable={true}
                            />
                        </VeboxForm.Section>
                    </div>
                    <FormCardFooter
                        onReset={resetValues}
                        onNext={goToNextStep}
                        disabled={!(isFormValid.all && isFormValid.emailValid && !isFormValid.emailAlreadyInUse && !isFormValid.usernameAlreadyInUse && isFormValid.usernameValid)}
                        loadable={true}
                        loading={loading}
                    />
                </VeBoxCard>
                <ProcessStageArrow
                    stepNumber={USER_INFO_STEP_NUMBER}
                    direction={ProcessStageArrowDirection.FORWARD}
                    visible={isFormValid.all && isFormValid.emailValid && !isFormValid.emailAlreadyInUse && isFormValid.usernameValid && !isFormValid.usernameAlreadyInUse && (processController.reachablePosition > USER_INFO_STEP_NUMBER - 1)}
                />
            </div>
        </ProcessStage>
    );
}

interface TextInputsWithErrorMessagesProps {
    isWrong: boolean,
    errorMessage: string,
}

const TextInputsWithErrorMessages: React.FC<TextInputsWithErrorMessagesProps & TextInputProps> = ({
                                                                                                      isWrong,
                                                                                                      errorMessage,
                                                                                                      ...textProps
                                                                                                  }) => {
    return (
        <VeboxForm.TextInput
            {...textProps}
            styles={{
                ...INPUT_BORDER_BOX_WITH_MARGIN,
                input: {
                    ...INPUT_BORDER_BOX.input,
                    borderColor: isWrong ? "#F00" : "#B4BCC3",
                }
            } as any}
            endInputAddon={
                isWrong ? (
                    <InputAddon position={"end"}>
                        <Tooltip message={errorMessage}>
                            <span className="material-icons-outlined" style={{color: "#F00"}}>error_outline</span>
                        </Tooltip>
                    </InputAddon>
                ) : undefined
            }
        />
    )
}
